<script lang="ts" setup>

import { ref } from 'vue'
import { supabase } from '../lib/supabase'
import { Session } from '@supabase/supabase-js';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useUserStore } from '@/stores/user';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import { useLoadingStore } from '../stores/loading'

const loading = ref(false)
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const errorMessage = ref('')
const successMessage = ref('')
const session = ref<Session | null>(null)
const router = useRouter();
const apiUrl = process.env.VUE_APP_DJANGO_BACKEND
const contactMessage = ref(false);
const isSigning = ref(false)
const showSecondPassword = ref(false)
const hcaptchaKey = process.env.VUE_APP_HCAPTCHA_SITE_KEY
const captchaToken = ref('')
const captchaRef = ref()
const loadingStore = useLoadingStore()

const onCaptchaVerify = (token: string) => {
  captchaToken.value = token
}

const onCaptchaExpired = () => {
  captchaToken.value = ''
}

const resetCaptcha = () => {
  if (captchaRef.value && typeof captchaRef.value.resetCaptcha === 'function') {
    captchaRef.value.resetCaptcha()
  }
}

const handleSignUp = async () => {
  errorMessage.value = ''
  successMessage.value = ''
  contactMessage.value = false

  if (!email.value) {
    errorMessage.value = "Please enter an email address.";
    return;
  }

  if (!password.value) {
    errorMessage.value = "Please enter a password.";
    return;
  }

  if (!isSigning.value) {
    isSigning.value = true
    showSecondPassword.value = true
    successMessage.value = "Please confirm your passowrd."
    return;
  }

  if (!captchaToken.value) {
    errorMessage.value = "Please complete the CAPTCHA."
    return
  }

  if (!confirmPassword.value) {
    errorMessage.value = "Please confirm your password.";
    return;
  }

  if (password.value !== confirmPassword.value) {
    errorMessage.value = "Passwords do not match."
    return
  }
  
  await checkIfUserExists();
  if (errorMessage.value) {
    return;
  }

  try {
    loading.value = true
    loadingStore.setLoading(true)
    const { data, error } = await supabase.auth.signUp({
      email: email.value,
      password: password.value,
      options: {
        captchaToken: captchaToken.value,
      }

    })
    resetCaptcha()
    if (error) { 
      throw error
    }

    successMessage.value = "Account created, check your email";
  } catch (error: any) {
    errorMessage.value = error.message
  } finally {
    loading.value = false
    loadingStore.setLoading(false)
  }
}

const checkIfUserExists = async () => {
  try {
    const response = await fetch(`${apiUrl}/api/users/exists/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.value })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.exists) {
      errorMessage.value = 'User with this email already exists.';
      contactMessage.value = true;
    }
  } catch (error) {
    console.error('Error checking if user exists:', error);
  }
};

const handleSignIn = async () => {

  errorMessage.value = ''
  successMessage.value = ''
  contactMessage.value = false
  
  
  if (isSigning.value) {
    resetForm()
    return;
  }

  const authStore = useAuthStore();
  const userStore = useUserStore();

  if (!email.value) {
    errorMessage.value = "Please enter an email address.";
    return;
  }

  if (!password.value) {
    errorMessage.value = "Please enter a password.";
    return;
  }

  try {
    loading.value = true
    const { data, error } = await supabase.auth.signInWithPassword({ 
      email: email.value,
      password: password.value,
      options: {
        captchaToken: captchaToken.value,
      }
    })
    if (error) throw new Error("Wrong credentials, can't log in? Contact us at support@coloring-ai.art");

    if (!data?.session) {
      throw new Error("No session data returned from supabase.");
    }
    authStore.setSession(data.session)
    userStore.fetchUser()
    router.push('/home')
    resetCaptcha()

  } catch (error: any) {
    errorMessage.value = error.message
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  isSigning.value = false
  showSecondPassword.value = false
  errorMessage.value = ''
  successMessage.value = ''
  contactMessage.value = false
  email.value = ''
  password.value = ''
  confirmPassword.value = ''
}
</script>

<template>
  <div class="auth-wrapper">
      <div class="left-panel">
          <div class="floating-elements">
              <div class="floating-element">🌈</div>
              <div class="floating-element">✨</div>
              <div class="floating-element">🎨</div>
          </div>
          <div class="brand-content">
              <div class="brand-logo">
                  <img src="../assets/images/Emma.jpg" alt="Coloring-AI">
              </div>
          </div>
      </div>
      
      <div class="right-panel">
          <div class="auth-header">
              <a href="/"><h2 class="auth-title green">Coloring-AI</h2></a>
              <p class="auth-subtitle">Sign in to continue :)</p>
          </div>

          <form @submit.prevent>
              <div class="form-group">
                  <label class="form-label">Email Address</label>
                  <input type="email" class="form-input" v-model="email" placeholder="Enter your email" required>
              </div>
              
              <div class="form-group">
                  <label class="form-label">Password</label>
                  <input type="password" class="form-input" v-model="password" placeholder="Enter your password" required>
              </div>

               <div v-if="showSecondPassword" class="form-group">
                  <label class="form-label">Confirm Password</label>
                  <input type="password" class="form-input" v-model="confirmPassword" placeholder="Confirm your password" required>
              </div>

              <div class="captcha-container">
                  <vue-hcaptcha
                      ref="captchaRef"
                      sitekey="679c8406-091a-44a6-b226-f9e48b18167e"
                      @verify="onCaptchaVerify"
                      @expired="onCaptchaExpired"
                  />
              </div>

              <div class="message error msg" v-if="errorMessage">{{ errorMessage }}</div>
              <div class="message success msg" v-if="successMessage">{{ successMessage }}</div>

              <div class="btn-group">
                  <button type="submit" class="btn btn-primary" @click="handleSignIn" :disabled="loading">
                      {{ loading ? 'Loading...' : 'Login' }}
                  </button>
                  <button type="button" class="btn btn-secondary" @click="handleSignUp" :disabled="loading">
                      Sign Up
                  </button>
              </div>
          </form>
      </div>
  </div>
</template>

<style>
.auth-wrapper {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  max-width: 1100px;
  min-height: 600px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;

}

.left-panel {
  background: linear-gradient(135deg, #578FCA 0%, #8EB486 100%);
  padding: 60px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.left-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="doodle" patternUnits="userSpaceOnUse" width="100" height="100"><path d="M10 10 Q20 5 30 10 T50 10" stroke="rgba(255,255,255,0.1)" stroke-width="2" fill="none"/><circle cx="70" cy="20" r="3" fill="rgba(255,255,255,0.1)"/><path d="M20 30 L30 25 L25 35 Z" fill="rgba(255,255,255,0.1)"/><circle cx="15" cy="70" r="2" fill="rgba(255,255,255,0.1)"/><path d="M60 60 Q70 55 80 60 T90 60" stroke="rgba(255,255,255,0.1)" stroke-width="1.5" fill="none"/><path d="M40 80 L50 75 L45 85 Z" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23doodle)"/></svg>');
  opacity: 0.3;
}

a {
  text-decoration: none;
}

.brand-content {
  text-align: center;
  position: relative;
  z-index: 2;
}

.msg {
  max-width: 200px;
  display: flex;
  justify-content: center;
  margin: auto;
}

.brand-logo {
  width: 300px;
  height: 300px;
  background: white;
  border-radius: 5%;
  margin: 0 auto 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.brand-logo img {
  width: 280px;
  height: 280px;
  object-fit: cover;
  border-radius: 15%;
}

.brand-title {
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
}

.brand-subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  max-width: 300px;
  margin: 0 auto;
}

.floating-elements {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.floating-element {
  position: absolute;
  opacity: 0.2;
  animation: float 6s ease-in-out infinite;
}

.floating-element:nth-child(1) {
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.floating-element:nth-child(2) {
  top: 60%;
  right: 15%;
  animation-delay: -2s;
}

.floating-element:nth-child(3) {
  bottom: 20%;
  left: 20%;
  animation-delay: -4s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
}

.right-panel {
  padding: 60px 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: white;
}

.auth-header {
  text-align: center;
  margin-bottom: 40px;
}

.auth-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.auth-subtitle {
  font-size: 1.1rem;
  color: #666;
}

.auth-tabs {
  display: flex;
  margin-bottom: 30px;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 4px;
}

.auth-tab {
  flex: 1;
  padding: 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #666;
}

.auth-tab.active {
  background: white;
  color: #333;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.form-input {
  width: 85%;
  padding: 16px 20px;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #fafbfc;
}

.form-input:focus {
  outline: none;
  border-color: #578FCA;
  background: white;
  box-shadow: 0 0 0 4px rgba(161, 227, 249, 0.1);
}

.captcha-container {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
}

.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.success {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.btn-group {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  margin-top: 30px;
}

.btn {
  flex: 1;
  padding: 16px;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  background:#8EB486;
  color: #333;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(111, 241, 137, 0.4);
}

.btn-secondary {
  background:#578FCA;
  color: #333;
}

.btn-secondary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(161, 227, 249, 0.4);
}

.divider {
  text-align: center;
  margin: 30px 0;
  position: relative;
  color: #666;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e1e5e9;
}

.divider span {
  background: white;
  padding: 0 20px;
  font-size: 0.9rem;
}

.social-login {
  display: flex;
  gap: 12px;
}

.social-btn {
  flex: 1;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: #666;
}

.social-btn:hover {
  border-color: #578FCA;
  background: #fafbfc;
}

/* Responsive Design */
@media (max-width: 768px) {
  .auth-wrapper {
      grid-template-columns: 1fr;
      max-width: 400px;
  }

  .left-panel {
      padding: 40px 20px;
      min-height: 300px;
  }

  .brand-title {
      font-size: 2rem;
  }

  .brand-subtitle {
      font-size: 1rem;
  }

  .right-panel {
      padding: 40px 30px;
  }

  .auth-title {
      font-size: 2rem;
  }

  .btn-group {
      flex-direction: column;
  }

  .social-login {
      flex-direction: column;
  }
}

@media (max-width: 480px) {
  body {
      padding: 10px;
  }

  .right-panel {
      padding: 30px 20px;
  }

  .form-input {
      font-size: 16px; /* Prevent zoom on iOS */
  }
}

</style>
